import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import axios from 'axios';
import { fetchAndSaveThumbnail } from "../../utils/verkadaHelpers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShippingModule = ({
  // Translation & Layout
  t,
  lang,
  isMobile,
  session,
  me,
  
  // Shipping state
  shippingAddress,
  setShippingAddress,
  shippingContent,
  setShippingContent,
  shippingEvents,
  setShippingEvents,
  shippingSearch,
  setShippingSearch,
  shippingSortBy,
  setShippingSortBy,
  shippingSortOrder,
  setShippingSortOrder,
  currentTrackingNumber,
  setCurrentTrackingNumber,
  savedShippingUID,
  savedShippingCameraID,
  
  // Modals
  setSelectedThumbnail,
  setSelectedEmailEvent,
  
  // Functions from parent
  loadShippingEvents
}) => {

  // Generate UPS-style tracking number
  const generateTrackingNumber = () => {
    const prefix = "1Z";
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = prefix;
    for (let i = 0; i < 16; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Generate international fake address
  const generateFakeAddress = () => {
    const addresses = [
      // Deutschland
      "Max Mustermann\nMusterstraße 123\n10115 Berlin\nDeutschland",
      "Anna Schmidt\nHauptstraße 45\n80331 München\nDeutschland",
      "Klaus Weber\nBahnhofstraße 78\n50667 Köln\nDeutschland",
      "Maria Fischer\nKirchweg 12\n20095 Hamburg\nDeutschland",
      // USA
      "John Smith\n123 Main Street\nNew York, NY 10001\nUSA",
      "Sarah Johnson\n456 Oak Avenue\nLos Angeles, CA 90001\nUSA",
      "Michael Brown\n789 Elm Street\nChicago, IL 60601\nUSA",
      "Emily Davis\n321 Maple Drive\nHouston, TX 77001\nUSA",
      // UK
      "James Wilson\n10 Downing Street\nLondon SW1A 2AA\nUnited Kingdom",
      "Emma Taylor\n25 High Street\nManchester M1 1AD\nUnited Kingdom",
      "Oliver Thomas\n42 Baker Street\nBirmingham B1 1AA\nUnited Kingdom",
      // Frankreich
      "Pierre Dubois\n15 Rue de la Paix\n75002 Paris\nFrance",
      "Marie Martin\n8 Avenue des Champs\n69001 Lyon\nFrance",
      // Spanien
      "Carlos García\n30 Calle Mayor\n28013 Madrid\nEspaña",
      "Isabel López\n18 Plaza Catalunya\n08002 Barcelona\nEspaña",
      // Italien
      "Marco Rossi\n7 Via Roma\n00100 Roma\nItalia",
      "Sofia Bianchi\n12 Corso Vittorio\n20121 Milano\nItalia",
      // Niederlande
      "Jan de Vries\n5 Damstraat\n1012 Amsterdam\nNetherlands",
      // Österreich
      "Wolfgang Müller\n20 Kärntner Straße\n1010 Wien\nÖsterreich",
      // Schweiz
      "Hans Meier\n33 Bahnhofstrasse\n8001 Zürich\nSchweiz",
      // Polen
      "Piotr Kowalski\n14 Ulica Floriańska\n31-019 Kraków\nPolska",
      "Anna Nowak\n28 Ulica Marszałkowska\n00-576 Warszawa\nPolska",
      // Portugal
      "João Silva\n45 Rua Augusta\n1100-053 Lisboa\nPortugal",
      "Maria Santos\n12 Avenida dos Aliados\n4000-066 Porto\nPortugal",
      // Griechenland
      "Dimitris Papadopoulos\n23 Ermou Street\n10563 Athens\nGreece",
      "Sofia Georgiou\n8 Aristotelous Square\n54623 Thessaloniki\nGreece",
      // Schweden
      "Lars Andersson\n16 Drottninggatan\n11151 Stockholm\nSverige",
      "Emma Johansson\n9 Avenyn\n41136 Göteborg\nSverige",
      // Norwegen
      "Ole Hansen\n22 Karl Johans gate\n0154 Oslo\nNorge",
      "Ingrid Larsen\n11 Bryggen\n5003 Bergen\nNorge",
      // Dänemark
      "Mads Jensen\n17 Strøget\n1160 København\nDanmark",
      "Sofie Nielsen\n6 Vestergade\n8000 Aarhus\nDanmark",
      // Finnland
      "Mikko Virtanen\n13 Aleksanterinkatu\n00100 Helsinki\nSuomi",
      "Aino Mäkinen\n21 Yliopistonkatu\n20100 Turku\nSuomi",
      // Belgien
      "Luc Dubois\n19 Grand Place\n1000 Bruxelles\nBelgique",
      "Sophie Laurent\n24 Meir\n2000 Antwerpen\nBelgië",
      // Tschechien
      "Pavel Novák\n31 Václavské náměstí\n11000 Praha\nČesko",
      "Jana Svobodová\n15 Masarykova\n60200 Brno\nČesko",
      // Irland
      "Patrick O'Brien\n27 Grafton Street\nD02 Dublin\nIreland",
      "Aoife Murphy\n18 Patrick Street\nT12 Cork\nIreland",
      // Kroatien
      "Ivan Horvat\n9 Ilica\n10000 Zagreb\nHrvatska",
      "Ana Kovač\n14 Diocletian Palace\n21000 Split\nHrvatska",
      // Rumänien
      "Andrei Popescu\n26 Calea Victoriei\n010061 București\nRomânia",
      "Elena Ionescu\n11 Strada Eroilor\n400129 Cluj-Napoca\nRomânia",
      // Ungarn
      "István Nagy\n35 Váci utca\n1052 Budapest\nMagyarország",
      "Katalin Tóth\n8 Kossuth tér\n6720 Szeged\nMagyarország"
    ];
    return addresses[Math.floor(Math.random() * addresses.length)];
  };

  // Generate random electronic/electrical devices as package content
  const generatePackageContent = () => {
    const devices = [
      "Laptop Dell XPS 15",
      "MacBook Pro 14 Zoll",
      "iPad Pro 12.9",
      "Samsung Galaxy Tablet",
      "iPhone 15 Pro",
      "Samsung Galaxy S24",
      "Bluetooth Kopfhörer Sony WH-1000XM5",
      "AirPods Pro 2",
      "Gaming-Maus Logitech G502",
      "Mechanische Tastatur Corsair K95",
      "USB-C Hub Anker 7-in-1",
      "Externe SSD Samsung T7 1TB",
      "Power Bank 20000mAh",
      "Smartwatch Apple Watch Series 9",
      "Fitbit Charge 6",
      "Kindle Paperwhite",
      "Nintendo Switch OLED",
      "PlayStation 5 Controller",
      "Webcam Logitech C920",
      "Ring Doorbell Pro",
      "Amazon Echo Dot 5",
      "Google Nest Mini",
      "Philips Hue Starter Kit",
      "LED-Strip 5m RGB",
      "Drohne DJI Mini 3 Pro",
      "GoPro Hero 12",
      "Canon EOS R50 Kamera",
      "Sony Alpha 7 IV",
      "Objektiv Canon RF 24-70mm",
      "Stativ Manfrotto Compact",
      "Mikrofon Blue Yeti",
      "Rode VideoMic Pro",
      "Monitor Dell UltraSharp 27 Zoll",
      "LG UltraWide 34 Zoll",
      "Grafiktablett Wacom Intuos",
      "3D-Drucker Creality Ender 3",
      "Raspberry Pi 5 Starter Kit",
      "Arduino Mega 2560",
      "E-Reader Tolino Vision 6",
      "Bluetooth Lautsprecher JBL Flip 6",
      "Sonos One SL",
      "Kaffeemaschine DeLonghi",
      "Wasserkocher Bosch",
      "Elektrische Zahnbürste Oral-B",
      "Haartrockner Dyson Supersonic",
      "Staubsauger Dyson V15",
      "Luftreiniger Philips AC2887",
      "Heizlüfter Rowenta",
      "Ventilator Dyson Pure Cool"
    ];
    
    // Generate random quantity (1-5 items)
    const quantity = Math.floor(Math.random() * 5) + 1;
    const selectedDevices = [];
    
    // Create a copy to avoid duplicate selection
    const availableDevices = [...devices];
    
    for (let i = 0; i < quantity; i++) {
      if (availableDevices.length === 0) break;
      const randomIndex = Math.floor(Math.random() * availableDevices.length);
      selectedDevices.push(availableDevices[randomIndex]);
      availableDevices.splice(randomIndex, 1);
    }
    
    return selectedDevices.map((device, index) => 
      `${index + 1}x ${device}`
    ).join('\n');
  };

  // Generate initial tracking number on mount
  useEffect(() => {
    if (!currentTrackingNumber) {
      setCurrentTrackingNumber(generateTrackingNumber());
    }
  }, []);

  // API Call to Verkada for shipping event
  const createShippingLabel = async () => {
    if (!shippingAddress.trim() || !shippingContent.trim()) {
      alert('Bitte Adresse und Paketinhalt eingeben');
      return;
    }

    if (!savedShippingUID || !savedShippingCameraID) {
      alert('Bitte Versandmodul in Einstellungen konfigurieren');
      return;
    }

    try {
      const trackingNumber = currentTrackingNumber;

      const berlinTime = new Date().toLocaleString('de-DE', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      const response = await axios.post(`${API}/verkada/shipping-event`, {
        attributes: {
          "Adresse": shippingAddress,
          "Position": shippingContent,
          "Tracking": trackingNumber
        },
        event_type_uid: savedShippingUID,
        camera_id: savedShippingCameraID,
        time_ms: Date.now()
      }, {
        headers: { 'X-User-Session-Token': session.userToken }
      });

      const data = response.data;
      
      if (data?.success) {
        alert(`✅ Versandlabel erfolgreich erstellt!\n\nTracking-Nr: ${trackingNumber}\nAdresse: ${shippingAddress}\nZeit: ${berlinTime}\n\n🎯 Verkada Video Tagging Event erfolgreich gesendet`);
        setShippingAddress('');
        setShippingContent('');
        setCurrentTrackingNumber(generateTrackingNumber());
        loadShippingEvents();
        
        // Wait 3 seconds then fetch thumbnail ONLY for this new label
        fetchAndSaveThumbnail({
          module: 'shipping',
          cameraId: savedShippingCameraID,
          timestampMs: data.timestamp_ms,
          savePayload: { tracking_number: trackingNumber },
          userToken: session.userToken,
          onSuccess: loadShippingEvents
        });
      } else if (data?.event_completed) {
        alert(`⚠️ Versandlabel erstellt (Verkada Integration teilweise)!\n\nTracking-Nr: ${trackingNumber}\nAdresse: ${shippingAddress}\nZeit: ${berlinTime}\n\n📋 Label: Erfolgreich\n🔗 Verkada: ${data.message}`);
        setShippingAddress('');
        setShippingContent('');
        setCurrentTrackingNumber(generateTrackingNumber());
        loadShippingEvents();
      } else {
        alert(`❌ Versandlabel-Erstellung fehlgeschlagen!\n\nFehler: ${data?.error || "Unbekannter Fehler"}`);
        return;
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || "Netzwerk- oder Server-Fehler";
      alert(`❌ Versandlabel-Erstellung fehlgeschlagen!\n\nFehler: ${errorMsg}\n\nBitte versuchen Sie es erneut oder wenden Sie sich an den Support.`);
      return;
    }
  };

  // Filter and sort shipping events
  const getFilteredAndSortedEvents = useMemo(() => {
    let filtered = [...shippingEvents];
    
    if (shippingSearch.trim()) {
      const searchLower = shippingSearch.toLowerCase();
      filtered = filtered.filter(event => 
        (event.tracking_number || '').toLowerCase().includes(searchLower) ||
        (event.address || '').toLowerCase().includes(searchLower) ||
        (event.content || '').toLowerCase().includes(searchLower)
      );
    }
    
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (shippingSortBy) {
        case 'timestamp':
          aVal = new Date(a.timestamp || 0).getTime();
          bVal = new Date(b.timestamp || 0).getTime();
          break;
        case 'tracking':
          aVal = (a.tracking_number || '').toLowerCase();
          bVal = (b.tracking_number || '').toLowerCase();
          break;
        case 'address':
          aVal = (a.address || '').toLowerCase();
          bVal = (b.address || '').toLowerCase();
          break;
        case 'content':
          aVal = (a.content || '').toLowerCase();
          bVal = (b.content || '').toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return shippingSortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return shippingSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [shippingEvents, shippingSearch, shippingSortBy, shippingSortOrder]);

  // Toggle sort order or change sort column
  const handleSort = (column) => {
    if (shippingSortBy === column) {
      setShippingSortOrder(shippingSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setShippingSortBy(column);
      setShippingSortOrder('desc');
    }
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
      
      {/* Shipping Header */}
      <Card className="panel lightCard" style={{marginTop: 8}}>
        <CardContent>
          <div className="panel-title darktxt">📦 {t('shipping_title')}</div>
          <div className="small">{t('shipping_subtitle')}</div>
        </CardContent>
      </Card>

      <div style={{
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', 
        gap: isMobile ? '12px' : '16px'
      }}>
        
        {/* Shipping Label Generator */}
        <Card className="panel lightCard">
          <CardContent>
            <div className="panel-title darktxt">{t('shipping_create_section')}</div>
            
            {/* Address Input */}
            <div style={{marginBottom: '16px'}}>
              <Label>{t('shipping_address_label')}</Label>
              <textarea
                value={shippingAddress}
                onChange={(e) => setShippingAddress(e.target.value)}
                placeholder={t('shipping_address_placeholder')}
                data-testid="shipping-address-input"
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
              <Button 
                variant="secondary" 
                style={{marginTop: '8px'}}
                onClick={() => setShippingAddress(generateFakeAddress())}
                data-testid="generate-address-btn"
              >
                {t('shipping_generate_address_btn')}
              </Button>
            </div>

            {/* Content Input */}
            <div style={{marginBottom: '16px'}}>
              <Label>{t('shipping_content_label')}</Label>
              <textarea
                value={shippingContent}
                onChange={(e) => setShippingContent(e.target.value)}
                placeholder={t('shipping_content_placeholder')}
                data-testid="shipping-content-input"
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
              <Button 
                variant="secondary" 
                style={{marginTop: '8px'}}
                onClick={() => setShippingContent(generatePackageContent())}
                data-testid="generate-content-btn"
              >
                {t('shipping_generate_content_btn')}
              </Button>
            </div>

            {/* Generate Button */}
            <div style={{marginBottom: '12px'}}>
              <Button 
                style={{width: '100%'}}
                disabled={!shippingAddress.trim() || !shippingContent.trim()}
                onClick={createShippingLabel}
                data-testid="create-shipping-label-btn"
              >
                {t('shipping_generate_btn')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* UPS Label Preview */}
        <Card className="panel lightCard">
          <CardContent>
            <div className="panel-title darktxt">{t('shipping_preview_section')}</div>
            
            {/* Realistic UPS Label Design */}
            <div style={{
              border: '3px solid #8B4513',
              borderRadius: '4px',
              padding: '20px',
              backgroundColor: '#FFFFFF',
              fontFamily: 'Arial, sans-serif',
              position: 'relative'
            }}>
              
              {/* UPS Header with Logo Area */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '3px solid #8B4513',
                paddingBottom: '12px',
                marginBottom: '20px',
                position: 'relative',
                zIndex: 2
              }}>
                <div style={{
                  fontSize: '28px',
                  fontWeight: 'bold',
                  color: '#8B4513',
                  letterSpacing: '2px'
                }}>UPS</div>
                <div style={{
                  fontSize: '14px',
                  color: '#666',
                  textAlign: 'right'
                }}>
                  <div>GROUND</div>
                  <div style={{fontSize: '12px'}}>TRACKING ID</div>
                </div>
              </div>

              {/* Service and Date Info */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '16px'
              }}>
                <div>
                  <div style={{fontSize: '10px', color: '#666'}}>SERVICE</div>
                  <div style={{fontSize: '12px', fontWeight: 'bold'}}>UPS GROUND</div>
                </div>
                <div>
                  <div style={{fontSize: '10px', color: '#666'}}>DELIVERY DATE</div>
                  <div style={{fontSize: '12px', fontWeight: 'bold'}}>
                    {new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString('de-DE')}
                  </div>
                </div>
              </div>

              {/* Tracking Number */}
              <div style={{marginBottom: '16px'}}>
                <div style={{fontSize: '11px', color: '#666', fontWeight: 'bold'}}>TRACKING NUMBER</div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  fontFamily: 'monospace',
                  backgroundColor: '#F8F8F8',
                  padding: '8px',
                  border: '2px solid #000',
                  letterSpacing: '1px'
                }}>
                  {currentTrackingNumber || "1Z999AA1012345675"}
                </div>
              </div>

              {/* Ship To Section */}
              <div style={{marginBottom: '16px'}}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: 'bold',
                  color: '#8B4513',
                  marginBottom: '6px',
                  borderBottom: '1px solid #ccc',
                  paddingBottom: '2px'
                }}>SHIP TO:</div>
                <div style={{
                  fontSize: '13px',
                  lineHeight: '1.4',
                  whiteSpace: 'pre-line',
                  fontWeight: '500'
                }}>
                  {shippingAddress || 'Max Mustermann\nMusterstraße 123\n12345 Berlin\nDeutschland'}
                </div>
              </div>

              {/* Ship From Section */}
              <div style={{marginBottom: '16px'}}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: 'bold',
                  color: '#8B4513',
                  marginBottom: '6px',
                  borderBottom: '1px solid #ccc',
                  paddingBottom: '2px'
                }}>SHIP FROM:</div>
                <div style={{fontSize: '13px', lineHeight: '1.4', fontWeight: '500'}}>
                  Helix Shipping Center<br/>
                  Solutions GmbH<br/>
                  Industriestraße 42<br/>
                  10115 Berlin, Deutschland
                </div>
              </div>

              {/* Package Details */}
              <div style={{marginBottom: '16px'}}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: 'bold',
                  color: '#8B4513',
                  marginBottom: '4px'
                }}>PACKAGE CONTENTS:</div>
                <div style={{
                  fontSize: '12px',
                  backgroundColor: '#f9f9f9',
                  padding: '6px',
                  border: '1px solid #ddd',
                  borderRadius: '3px'
                }}>
                  {shippingContent || 'Elektronische Bauteile und Zubehör'}
                </div>
              </div>

              {/* Weight and Reference */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginBottom: '16px',
                fontSize: '11px'
              }}>
                <div>
                  <div style={{color: '#666', fontWeight: 'bold'}}>WEIGHT</div>
                  <div>{Math.floor(Math.random() * 5 + 1)}.{Math.floor(Math.random() * 9)} kg</div>
                </div>
                <div>
                  <div style={{color: '#666', fontWeight: 'bold'}}>REF #</div>
                  <div>PKG{Math.floor(Math.random() * 9999).toString().padStart(4, '0')}</div>
                </div>
              </div>

              {/* Barcode Area */}
              <div style={{
                borderTop: '2px solid #8B4513',
                paddingTop: '12px',
                textAlign: 'center',
                backgroundColor: '#fafafa'
              }}>
                <div style={{
                  fontSize: '32px',
                  fontFamily: 'monospace',
                  letterSpacing: '1px',
                  color: '#000',
                  marginBottom: '4px'
                }}>||||| |||| |||||</div>
                <div style={{
                  fontSize: '8px',
                  color: '#666',
                  letterSpacing: '3px'
                }}>(01) 00012345678906</div>
                <div style={{fontSize: '10px', color: '#666', marginTop: '4px'}}>
                  MAXICODE™
                </div>
              </div>

              {/* UPS Ground Logo Area */}
              <div style={{
                textAlign: 'center',
                marginTop: '8px',
                fontSize: '10px',
                color: '#8B4513',
                fontWeight: 'bold'
              }}>
                ® UPS GROUND
              </div>
            </div>

            {/* Settings Status */}
            <div style={{marginTop: '16px'}}>
              {(!savedShippingUID || !savedShippingCameraID) && (
                <div style={{
                  padding: '8px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#92400e'
                }}>
                  ⚠️ Versandmodul in Einstellungen konfigurieren
                </div>
              )}
              {savedShippingUID && savedShippingCameraID && (
                <div style={{
                  padding: '8px',
                  backgroundColor: '#d1fae5',
                  border: '1px solid #10b981',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#065f46'
                }}>
                  ✅ Versandmodul konfiguriert
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Shipping Events History Table - Full Width Below */}
        <Card className="panel lightCard" style={{gridColumn: isMobile ? '1' : '1 / -1'}}>
          <CardContent>
            <div className="panel-title darktxt">📋 {t('shipping_history_title')}</div>
            <div className="small" style={{marginBottom: '12px'}}>{t('shipping_history_subtitle')}</div>
            
            {/* Search Field */}
            <div style={{marginBottom: '16px'}}>
              <input
                type="text"
                placeholder={t('shipping_search_placeholder')}
                value={shippingSearch}
                onChange={(e) => setShippingSearch(e.target.value)}
                data-testid="shipping-search-input"
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div style={{overflowX: 'auto'}}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px'
              }}>
                <thead>
                  <tr style={{
                    borderBottom: '2px solid #e5e7eb',
                    backgroundColor: '#f9fafb'
                  }}>
                    <th 
                      onClick={() => handleSort('timestamp')}
                      style={{
                        padding: '12px 8px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        minWidth: '140px',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}>
                      {t('shipping_table_datetime')} {shippingSortBy === 'timestamp' && (shippingSortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      onClick={() => handleSort('tracking')}
                      style={{
                        padding: '12px 8px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        minWidth: '180px',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}>
                      {t('shipping_table_tracking')} {shippingSortBy === 'tracking' && (shippingSortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      onClick={() => handleSort('address')}
                      style={{
                        padding: '12px 8px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        minWidth: '200px',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}>
                      {t('shipping_table_address')} {shippingSortBy === 'address' && (shippingSortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      onClick={() => handleSort('content')}
                      style={{
                        padding: '12px 8px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        minWidth: '200px',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}>
                      {t('shipping_table_content')} {shippingSortBy === 'content' && (shippingSortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      minWidth: '100px'
                    }}>{t('shipping_table_thumbnail')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shippingEvents.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{
                        padding: '24px',
                        textAlign: 'center',
                        color: '#6b7280'
                      }}>
                        {t('shipping_table_empty')}
                      </td>
                    </tr>
                  ) : getFilteredAndSortedEvents.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{
                        padding: '24px',
                        textAlign: 'center',
                        color: '#6b7280'
                      }}>
                        {t('shipping_search_no_results')}
                      </td>
                    </tr>
                  ) : (
                    getFilteredAndSortedEvents.map((event, index) => {
                      let formattedDate = '';
                      let formattedTime = '';
                      if (event.timestamp) {
                        const date = new Date(event.timestamp);
                        formattedTime = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                        formattedDate = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
                      }
                      
                      return (
                        <tr 
                          key={event._id || index} 
                          data-testid={`shipping-event-row-${index}`}
                          style={{
                            borderBottom: '1px solid #e5e7eb',
                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                            cursor: 'pointer'
                          }}
                          onClick={() => setSelectedEmailEvent(event)}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                        >
                          <td style={{padding: '12px 8px'}}>
                            <div style={{lineHeight: '1.4'}}>
                              <div style={{fontWeight: '500'}}>{formattedTime}</div>
                              <div style={{fontSize: '12px', color: '#6b7280'}}>{formattedDate}</div>
                            </div>
                          </td>
                          <td style={{padding: '12px 8px', fontFamily: 'monospace', fontSize: '12px'}}>
                            {event.tracking_number || '-'}
                          </td>
                          <td style={{padding: '12px 8px'}}>
                            <div style={{
                              maxWidth: '250px',
                              whiteSpace: 'pre-line',
                              fontSize: '12px',
                              lineHeight: '1.4'
                            }}>
                              {event.address || '-'}
                            </div>
                          </td>
                          <td style={{padding: '12px 8px'}}>
                            <div style={{
                              maxWidth: '250px',
                              whiteSpace: 'pre-line',
                              fontSize: '12px',
                              lineHeight: '1.4'
                            }}>
                              {event.content || '-'}
                            </div>
                          </td>
                          <td style={{padding: '12px 8px'}}>
                            {event.thumbnail ? (
                              <img 
                                src={event.thumbnail} 
                                alt="Thumbnail"
                                style={{
                                  width: '80px',
                                  height: '60px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  border: '1px solid #e5e7eb'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedThumbnail(event.thumbnail);
                                }}
                              />
                            ) : (
                              <span style={{color: '#9ca3af', fontSize: '11px'}}>📷 Kein Bild</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ShippingModule;
